import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import axiosInstance from "../../services/API";

const CHUNK_SIZE = 5 * 1024 * 1024;
const MAX_PART_RETRIES = 2;

const extractUploadId = (data) => data?.upload_id || data?.session_id || null;

const extractPartNumber = (part, fallbackIndex) => part?.part_number || part?.partNumber || fallbackIndex + 1;

const extractPresignedUrl = (part) => part?.url || part?.presigned_url || "";

const normalizeETag = (etag) => {
    if (!etag) return "";
    return etag.replace(/^"|"$/g, "");
};

const isLikelyCorsOrPreflightError = (error) => {
    const message = error?.message || "";
    // Browser blocks CORS failures before response reaches JS, so axios has no response.
    return !error?.response && message === "Network Error";
};

const uploadPartWithRetry = async ({ url, chunk, contentType, signal, retries = MAX_PART_RETRIES }) => {
    let attempt = 0;
    while (attempt <= retries) {
        try {
            const response = await axios.put(url, chunk, {
                headers: {
                    "Content-Type": contentType,
                },
                signal,
            });

            return response;
        } catch (error) {
            if (isLikelyCorsOrPreflightError(error)) {
                throw new Error("S3 CORS preflight failed. Configure bucket CORS to allow PUT/OPTIONS from the web origin.");
            }
            if (signal?.aborted || attempt === retries) {
                throw error;
            }
            attempt += 1;
        }
    }
};

export const Uploadvideo = createAsyncThunk("Uploadvideo", async (data) => {
    try {
        const { file, ...serializableData } = data || {};
        const response = serializableData;
        return response;
    } catch (error) {
        return error;
    }
});

export const uploadVideoToS3 = createAsyncThunk(
    "uploadVideoToS3",
    async ({ file }, { dispatch, signal, rejectWithValue }) => {
        let uploadId = null;
        let s3Key = null;

        try {
            const totalParts = Math.ceil(file.size / CHUNK_SIZE) || 1;

            const initiateResponse = await axiosInstance.post(`/post/upload/initiate/`, {
                filename: file.name,
                total_parts: totalParts,
                content_type: file.type,
            });

            const initiateData = initiateResponse?.data || {};
            uploadId = extractUploadId(initiateData);
            s3Key = initiateData?.key || initiateData?.s3_key;
            const presignedUrls = initiateData?.presigned_urls || [];

            if (!uploadId || !Array.isArray(presignedUrls) || presignedUrls.length === 0) {
                throw new Error("Upload session is invalid. Missing upload_id or presigned URLs.");
            }

            dispatch(setUploadSession({ uploadId, s3Key: s3Key || "" }));

            const sortedUrls = [...presignedUrls].sort(
                (a, b) => (extractPartNumber(a, 0) || 0) - (extractPartNumber(b, 0) || 0)
            );
            const uploadedParts = [];
            const totalUploadParts = sortedUrls.length;

            for (let index = 0; index < sortedUrls.length; index += 1) {
                if (signal?.aborted) {
                    throw new Error("Upload aborted");
                }

                const currentPart = sortedUrls[index];
                const partNumber = extractPartNumber(currentPart, index);
                const partUrl = extractPresignedUrl(currentPart);
                if (!partUrl) {
                    throw new Error(`Missing presigned URL for part ${partNumber}`);
                }

                const start = (partNumber - 1) * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                const partResponse = await uploadPartWithRetry({
                    url: partUrl,
                    chunk,
                    contentType: file.type || "application/octet-stream",
                    signal,
                });

                const etag = normalizeETag(partResponse?.headers?.etag || partResponse?.headers?.ETag);
                if (!etag) {
                    throw new Error(`Missing ETag for part ${partNumber}`);
                }

                uploadedParts.push({
                    PartNumber: partNumber,
                    ETag: etag,
                });

                const progress = Math.min(99, Math.round((uploadedParts.length * 100) / totalUploadParts));
                dispatch(setUploadProgress(progress));
            }

            const completePayload = {
                upload_id: uploadId,
                session_id: uploadId,
                parts: uploadedParts,
            };
            if (s3Key) {
                completePayload.s3_key = s3Key;
            }

            const completeResponse = await axiosInstance.post(`/post/upload/complete/`, completePayload);
            const completeData = completeResponse?.data || {};

            return {
                upload_id: uploadId,
                s3_key: completeData?.s3_key || s3Key,
                video_url: completeData?.video_url || null,
                parts: uploadedParts,
            };
        } catch (error) {
            if (uploadId) {
                try {
                    const abortPayload = {
                        upload_id: uploadId,
                        session_id: uploadId,
                    };
                    if (s3Key) {
                        abortPayload.s3_key = s3Key;
                    }

                    await axiosInstance.post(`/post/upload/abort/`, abortPayload);
                } catch (abortError) {
                    // Best-effort cleanup only.
                    console.error("[S3 Upload] Abort: Abort request failed", {
                        uploadId,
                        error: abortError?.response?.data || abortError?.message,
                    });
                }
            }

            if (signal?.aborted) {
                console.warn("[S3 Upload] Upload cancelled by user/component");
                return rejectWithValue("Upload aborted");
            }
            if (isLikelyCorsOrPreflightError(error) || String(error?.message || "").includes("CORS preflight")) {
                return rejectWithValue("S3 CORS preflight blocked the upload. Ask backend/DevOps to allow PUT/OPTIONS for your frontend origin in the S3 bucket CORS config.");
            }
            console.error("[S3 Upload] Upload failed", {
                uploadId,
                error: error?.response?.data || error?.message,
            });
            return rejectWithValue(error?.response?.data?.detail || error?.message || "Upload failed");
        }
    }
);

export const uploadVideoData = createAsyncThunk(
    "uploadVideoData",
    async (data, { dispatch }) => {
        const form_data = new FormData();
        form_data.append("body", data.body);
        form_data.append("s3_key", data.s3_key);
        form_data.append("is_private", data.is_private ? "True" : "False");
        form_data.append("comments_enabled", data.comments_enabled ? "True" : "False");
        form_data.append("download_enabled", data.download_enabled ? "True" : "False");
        if (data.thumbnail) {
            form_data.append("thumbnail", data.thumbnail);
        }
        form_data.append("is_created_video", data.is_created_video ? "True" : "False");
        if (data.duration) {
            form_data.append("duration", data.duration);
        }

        const response = await axiosInstance.post(`/post/create/`, form_data, {
            headers: {
                "content-type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                dispatch(setUploadProgress(percentCompleted));
            },
        });

        try {
            const result = response.data;
            return result;
        } catch (error) {
            return error;
        }
    }
);

export const fetchVideoStatus = createAsyncThunk(
    "fetchVideoStatus",
    async (postId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/post/${postId}/video-status/`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data || error.message);
        }
    }
);

export const UploadvideoSlice = createSlice({
    name: "Uploadvideo",
    initialState: {
        SelectedVideo: {},
        loading: false,
        error: null,
        progress: 0,
        uploadId: null,
        s3Key: "",
        // Own in-flight uploads awaiting/failing moderation, keyed by post id.
        // Kept client-side so a post stays visible to its uploader even if a
        // list refetch (e.g. the public-posts endpoint) omits pending/rejected posts.
        pendingUploads: {},
    },
    reducers: {
        setUploadProgress: (state, action) => {
            state.progress = action.payload;
        },
        setUploadSession: (state, action) => {
            state.uploadId = action.payload?.uploadId || null;
            state.s3Key = action.payload?.s3Key || "";
        },
        clearSelectedVideo: (state) => {
            state.SelectedVideo = {};
            state.progress = 0;
            state.uploadId = null;
            state.s3Key = "";
            state.error = null;
        },
        addPendingUpload: (state, action) => {
            const post = action.payload;
            if (!post?.id) return;
            // Respect a moderation decision already present on the create response
            // instead of assuming "pending" (some rejections resolve synchronously).
            state.pendingUploads[post.id] = { post, status: post.moderation_status || "pending" };
        },
        removePendingUpload: (state, action) => {
            delete state.pendingUploads[action.payload];
        },
    },
    extraReducers: (builder) => {
        // AccountProfile action promises
        builder.addCase(Uploadvideo.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(Uploadvideo.fulfilled, (state, action) => {
            state.SelectedVideo = action.payload;
            state.loading = false;
        });
        builder.addCase(Uploadvideo.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        /* uploadVideoData  */
        builder.addCase(uploadVideoData.pending, (state) => {
            state.loading = true;
            state.progress = 0;
        });
        builder.addCase(uploadVideoData.fulfilled, (state, action) => {
            state.loading = false;
            state.progress = 100;
        });
        builder.addCase(uploadVideoData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.progress = 0;
        });

        builder.addCase(uploadVideoToS3.pending, (state) => {
            state.loading = true;
            state.progress = 0;
            state.error = null;
            state.s3Key = "";
        });
        builder.addCase(uploadVideoToS3.fulfilled, (state, action) => {
            state.loading = false;
            state.progress = 100;
            state.uploadId = action.payload?.upload_id || null;
            state.s3Key = action.payload?.s3_key || "";
        });
        builder.addCase(uploadVideoToS3.rejected, (state, action) => {
            state.loading = false;
            state.progress = 0;
            state.error = action.payload || "Upload failed";
            state.uploadId = null;
            state.s3Key = "";
        });

        builder.addCase(fetchVideoStatus.fulfilled, (state, action) => {
            const postId = action.meta.arg;
            const entry = state.pendingUploads[postId];
            if (!entry) return;

            const status = action.payload?.moderation_status;
            if (!status) return;
            // Leave "approved" entries in place — AccountPage reacts to the
            // transition (refetches the public list) and clears it after.
            entry.status = status;
        });
    },
});

export default UploadvideoSlice.reducer;
export const { setUploadProgress, setUploadSession, clearSelectedVideo, addPendingUpload, removePendingUpload } = UploadvideoSlice.actions;
